/** Shared helpers for the synced viewer. */

import { GpsSummary } from '../../types';

export function haversineMeters(
  lat1: number, lon1: number, lat2: number, lon2: number,
): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180)
    * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Find the track index whose lat/lon is closest to (lat, lon). Returns
 * -1 if nothing within `maxMeters`. */
export function nearestTrackPointToLatLon(
  track: GpsSummary['track'], lat: number, lon: number, maxMeters: number,
): number {
  let bestIdx = -1;
  let bestM = maxMeters;
  for (let i = 0; i < track.length; i += 1) {
    const [tlat, tlon] = track[i];
    const m = haversineMeters(lat, lon, tlat, tlon);
    if (m < bestM) {
      bestM = m;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Find the sample whose UTC is closest to `utcMs`. Generic over any
 * shape that has a `.t` (UTC ms) field — the common case for recharts
 * data arrays. Used by Speed/IMU click-to-seek (H4) so the seek lands
 * on a real sample's timestamp, not on a between-samples chart
 * coordinate where the UI rounds inconsistently with Map (Haversine
 * snap) vs Speed/IMU (direct activeLabel).
 *
 * O(n) linear scan — fine for the chart sizes we render (≤ 5000
 * samples per IMU pane, 1 Hz Speed series).
 */
export function nearestSampleByUtc<T extends { t: number }>(
  samples: T[], utcMs: number,
): T | null {
  if (samples.length === 0) return null;
  let best = samples[0];
  let bestDelta = Math.abs(best.t - utcMs);
  for (let i = 1; i < samples.length; i += 1) {
    const delta = Math.abs(samples[i].t - utcMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = samples[i];
    }
  }
  return best;
}

export function formatUtcMs(utcMs: number | null | undefined): string {
  if (utcMs == null) return '—';
  return new Date(utcMs).toISOString().replace('T', ' ').replace('Z', 'Z');
}

/** Compass bearing (degrees clockwise from north) from point 1 → point 2. */
export function bearingDeg(
  lat1: number, lon1: number, lat2: number, lon2: number,
): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
}

/** Interpolate playhead position + heading along a track at `utcMs`.
 *
 * Binary-searches the bracketing segment in O(log n). Heading uses the
 * containing segment's bearing. Returns null when `utcMs` is more than
 * `OUT_OF_RANGE_TOL_MS` outside the track span — so the caller hides the
 * arrow rather than parking it on the start/finish vertex (which falsely
 * reads as "car sitting there", e.g. during video pre-roll when the
 * player clock leads the first GPS fix). Within the tolerance the
 * playhead clamps to the nearest endpoint so normal end-of-playback
 * overrun keeps the arrow pinned to the finish.
 */
const OUT_OF_RANGE_TOL_MS = 5000;

export function interpolatePlayhead(
  track: GpsSummary['track'], utcMs: number,
): { pos: [number, number]; heading: number } | null {
  const t = track;
  if (t.length === 0) return null;
  if (t.length === 1) return { pos: [t[0][0], t[0][1]], heading: 0 };
  if (utcMs < t[0][2] - OUT_OF_RANGE_TOL_MS) return null;
  if (utcMs > t[t.length - 1][2] + OUT_OF_RANGE_TOL_MS) return null;
  if (utcMs <= t[0][2]) {
    return {
      pos: [t[0][0], t[0][1]],
      heading: bearingDeg(t[0][0], t[0][1], t[1][0], t[1][1]),
    };
  }
  if (utcMs >= t[t.length - 1][2]) {
    const last = t[t.length - 1];
    const prev = t[t.length - 2];
    return {
      pos: [last[0], last[1]],
      heading: bearingDeg(prev[0], prev[1], last[0], last[1]),
    };
  }
  let lo = 0;
  let hi = t.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (t[mid][2] <= utcMs) lo = mid;
    else hi = mid - 1;
  }
  const a = t[lo];
  const b = t[Math.min(lo + 1, t.length - 1)];
  const span = b[2] - a[2];
  const f = span > 0 ? (utcMs - a[2]) / span : 0;
  return {
    pos: [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f],
    heading: bearingDeg(a[0], a[1], b[0], b[1]),
  };
}

/** Build a Leaflet DivIcon for the playhead arrow, rotated to `headingDeg`.
 * Caller passes the dynamically-loaded `L` module. */
export function playheadDivIcon(L: any, headingDeg: number): any {
  const html = `
    <div style="
      width:24px;height:24px;
      transform: rotate(${headingDeg.toFixed(1)}deg);
      transform-origin:center;
    ">
      <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="11" fill="#0f1015" stroke="#00c8ff" stroke-width="2"/>
        <path d="M12 4 L17 16 L12 13 L7 16 Z" fill="#00c8ff" stroke="#0f1015" stroke-width="0.5"/>
      </svg>
    </div>`;
  return L.divIcon({
    html,
    className: 'mp-playhead-arrow',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

/** Sweep-merge a list of [start, end] ranges (possibly overlapping or
 * unsorted) into the minimum sorted set of non-overlapping intervals.
 * Mirrors backend's _merge_coverage_ranges (backend/app/api/streams.py:90)
 * so a stitched run produced by unioning several sessions' /coverage
 * payloads collapses cleanly. */
export function mergeCoverageRanges(
  ranges: ReadonlyArray<readonly [number, number]>,
): Array<[number, number]> {
  if (ranges.length === 0) return [];
  const sorted = ranges
    .filter((r) => Number.isFinite(r[0]) && Number.isFinite(r[1]) && r[1] >= r[0])
    .map((r) => [r[0], r[1]] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  if (sorted.length === 0) return [];
  const merged: Array<[number, number]> = [[sorted[0][0], sorted[0][1]]];
  for (let i = 1; i < sorted.length; i += 1) {
    const [s, e] = sorted[i];
    const last = merged[merged.length - 1];
    if (s <= last[1]) {
      if (e > last[1]) last[1] = e;
    } else {
      merged.push([s, e]);
    }
  }
  return merged;
}

/** Binary search a sorted, non-overlapping [start, end] range list. Returns
 * true iff `tsMs` falls inside any range. O(log n). */
export function isCovered(
  tsMs: number, ranges: ReadonlyArray<readonly [number, number]>,
): boolean {
  if (ranges.length === 0) return false;
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const [s, e] = ranges[mid];
    if (tsMs < s) hi = mid - 1;
    else if (tsMs > e) lo = mid + 1;
    else return true;
  }
  return false;
}

export interface PolylineProjection {
  /** s-coordinate (meters from polyline start) of the foot of perpendicular. */
  s_m: number;
  /** Perpendicular distance from the input point to the polyline (meters). */
  d_m: number;
  /** Index of the segment (`[segIndex, segIndex+1]`) the foot lands on. */
  segIndex: number;
  /** Lat/lon of the foot of perpendicular (the projected point on the polyline). */
  foot: [number, number];
}

/** Project a (lat, lon) point onto a polyline; return s-coordinate (meters
 * from the start), perpendicular distance, and the foot of perpendicular.
 *
 * O(N) scan over polyline vertices. Tangent-plane projection on each
 * segment using a per-segment mean-latitude cosine — accurate to a few
 * cm at stage scales (≤ tens of km), which is well below the GPS noise
 * we care about. Callers pass `cumulativeS[i]` (meters from start at
 * vertex i) so the s-coordinate is read off without a re-scan.
 */
export function nearestPointOnPolyline(
  coords: ReadonlyArray<readonly [number, number]>,
  cumulativeS: ReadonlyArray<number>,
  lat: number,
  lon: number,
): PolylineProjection | null {
  if (coords.length < 2 || cumulativeS.length !== coords.length) return null;
  let bestD2 = Infinity;
  let bestS = 0;
  let bestFoot: [number, number] = [coords[0][0], coords[0][1]];
  let bestSeg = 0;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const a = coords[i];
    const b = coords[i + 1];
    const meanLat = ((a[0] + b[0]) / 2) * (Math.PI / 180);
    const cosMean = Math.cos(meanLat);
    const mPerDegLat = 111_320;
    const mPerDegLon = mPerDegLat * cosMean;
    const ax = a[1] * mPerDegLon;
    const ay = a[0] * mPerDegLat;
    const bx = b[1] * mPerDegLon;
    const by = b[0] * mPerDegLat;
    const px = lon * mPerDegLon;
    const py = lat * mPerDegLat;
    const dx = bx - ax;
    const dy = by - ay;
    const segLen2 = dx * dx + dy * dy;
    let t = 0;
    if (segLen2 > 0) {
      t = ((px - ax) * dx + (py - ay) * dy) / segLen2;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const fx = ax + t * dx;
    const fy = ay + t * dy;
    const ex = px - fx;
    const ey = py - fy;
    const d2 = ex * ex + ey * ey;
    if (d2 < bestD2) {
      bestD2 = d2;
      const segLen = cumulativeS[i + 1] - cumulativeS[i];
      bestS = cumulativeS[i] + t * segLen;
      // Convert foot back to lat/lon.
      bestFoot = [fy / mPerDegLat, fx / mPerDegLon];
      bestSeg = i;
    }
  }
  return {
    s_m: bestS,
    d_m: Math.sqrt(bestD2),
    segIndex: bestSeg,
    foot: bestFoot,
  };
}

/** Cumulative haversine length along a polyline. `cumulativeS[i]` is the
 * distance from `coords[0]` to `coords[i]`. */
export function cumulativeSMeters(
  coords: ReadonlyArray<readonly [number, number]>,
): number[] {
  if (coords.length === 0) return [];
  const out: number[] = [0];
  for (let i = 1; i < coords.length; i += 1) {
    const [lat1, lon1] = coords[i - 1];
    const [lat2, lon2] = coords[i];
    out.push(out[i - 1] + haversineMeters(lat1, lon1, lat2, lon2));
  }
  return out;
}

/** Perpendicular distance (meters) from point `p` to the great-circle
 * segment `a → b`. Uses an equirectangular small-region projection — fine
 * for the segment lengths we deal with (≤ a few hundred m between RDP
 * vertices). */
function perpendicularDistanceMeters(
  p: [number, number, number, number],
  a: [number, number, number, number],
  b: [number, number, number, number],
): number {
  const meanLat = ((a[0] + b[0]) / 2) * (Math.PI / 180);
  const cosMean = Math.cos(meanLat);
  // Project to local meters (rough but consistent for small spans).
  const ax = a[1] * cosMean * 111320;
  const ay = a[0] * 111320;
  const bx = b[1] * cosMean * 111320;
  const by = b[0] * 111320;
  const px = p[1] * cosMean * 111320;
  const py = p[0] * 111320;
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) {
    const ex = px - ax;
    const ey = py - ay;
    return Math.sqrt(ex * ex + ey * ey);
  }
  const num = Math.abs(dy * px - dx * py + bx * ay - by * ax);
  return num / Math.sqrt(len2);
}

/** Ramer-Douglas-Peucker simplification with an epsilon (meters) and a
 * hard `maxPoints` cap. If the first pass overshoots, epsilon is doubled
 * up to 5 times. Endpoints are always preserved. */
export function simplifyTrackRDP(
  track: GpsSummary['track'],
  epsilonMeters: number = 3,
  maxPoints: number = 4096,
): GpsSummary['track'] {
  if (track.length <= 2) return track;
  let eps = Math.max(epsilonMeters, 0.1);
  let result = track;
  for (let attempt = 0; attempt < 6; attempt += 1) {
    result = rdpRecurse(track, eps);
    if (result.length <= maxPoints) return result;
    eps *= 2;
  }
  // Hard fallback: uniformly downsample to maxPoints, preserving endpoints.
  if (result.length <= maxPoints) return result;
  const out: GpsSummary['track'] = [];
  const step = (result.length - 1) / (maxPoints - 1);
  for (let i = 0; i < maxPoints - 1; i += 1) {
    out.push(result[Math.floor(i * step)]);
  }
  out.push(result[result.length - 1]);
  return out;
}

function rdpRecurse(
  pts: GpsSummary['track'], epsilonMeters: number,
): GpsSummary['track'] {
  if (pts.length <= 2) return pts;
  let maxDist = 0;
  let maxIdx = 0;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const d = perpendicularDistanceMeters(pts[i], pts[0], pts[pts.length - 1]);
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }
  if (maxDist <= epsilonMeters) {
    return [pts[0], pts[pts.length - 1]];
  }
  const left = rdpRecurse(pts.slice(0, maxIdx + 1), epsilonMeters);
  const right = rdpRecurse(pts.slice(maxIdx), epsilonMeters);
  return left.slice(0, -1).concat(right);
}

/**
 * Centripetal Catmull-Rom smoothing of a [lat, lon] polyline, resampled
 * at ~`stepMeters` spacing. Turns a coarse organizer raceline (often
 * hand-clicked, with visible polygon corners) into a smooth curve that
 * reads like a real track.
 *
 * Centripetal (alpha = 0.5) — NOT uniform — because uniform Catmull-Rom
 * overshoots and self-loops at tight corners, and circuits have
 * hairpins. Centripetal is the variant that provably stays cusp- and
 * loop-free within each segment.
 *
 * Closed-loop aware: when the endpoints land within
 * `closeLoopToleranceM` of each other the line is treated as a ring —
 * neighbor points wrap around the seam so the start/finish joint is as
 * smooth as every other corner, and the output closes exactly (last
 * point === first point). Open lines clamp the spline at their
 * endpoints (endpoint positions are preserved verbatim).
 *
 * The spline interpolates — every input vertex lies on the output
 * curve — so cumulative-s / projection math downstream sees the same
 * track, just denser. Output is capped at `maxPoints` by widening the
 * effective step (matches the 4096-point trail cap elsewhere).
 */
export function smoothCatmullRom(
  latlon: ReadonlyArray<readonly [number, number]>,
  opts: {
    stepMeters?: number;
    maxPoints?: number;
    closeLoopToleranceM?: number;
  } = {},
): Array<[number, number]> {
  const stepMeters = Math.max(opts.stepMeters ?? 2.5, 0.5);
  const maxPoints = Math.max(opts.maxPoints ?? 4096, 16);
  const closeLoopToleranceM = opts.closeLoopToleranceM ?? 30;

  // Project to local planar meters (equirectangular about the mean
  // latitude — same approximation perpendicularDistanceMeters uses;
  // circuits/stages are far too small for it to matter). Dedupe
  // near-coincident vertices while projecting: zero-length segments
  // make the centripetal knot spacing collapse (t[i+1] === t[i] →
  // division by zero).
  let meanLat = 0;
  for (const [lat] of latlon) meanLat += lat;
  meanLat /= Math.max(latlon.length, 1);
  const cosMean = Math.cos((meanLat * Math.PI) / 180);
  const toXY = ([lat, lon]: readonly [number, number]): [number, number] => [
    lon * cosMean * 111320,
    lat * 111320,
  ];
  const toLatLon = ([x, y]: readonly [number, number]): [number, number] => [
    y / 111320,
    x / (cosMean * 111320),
  ];
  const pts: Array<[number, number]> = [];
  for (const p of latlon) {
    const xy = toXY(p);
    const prev = pts[pts.length - 1];
    if (prev) {
      const dx = xy[0] - prev[0];
      const dy = xy[1] - prev[1];
      if (dx * dx + dy * dy < 0.01 * 0.01) continue;
    }
    pts.push(xy);
  }
  if (pts.length < 3) {
    return latlon.map(([lat, lon]) => [lat, lon]);
  }

  // Ring detection. A closed input may or may not duplicate its first
  // vertex at the end — normalize to "no duplicate" and remember.
  const first = pts[0];
  const last = pts[pts.length - 1];
  const seamDx = first[0] - last[0];
  const seamDy = first[1] - last[1];
  const seamDist = Math.sqrt(seamDx * seamDx + seamDy * seamDy);
  const closed = seamDist <= closeLoopToleranceM;
  if (closed && seamDist < 0.01) pts.pop();
  if (pts.length < 3) {
    return latlon.map(([lat, lon]) => [lat, lon]);
  }

  const dist = (a: readonly [number, number], b: readonly [number, number]): number => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Pre-subdivide long chords with collinear midpoints (≤ 50 m apart).
  // A Catmull-Rom curve only interpolates its control points — a 300 m
  // straight described by 2 vertices would BOW sideways under the pull
  // of its corner neighbors. Collinear interior points pin straights
  // straight while corners (whose vertices sit close together) still
  // round into curves.
  const SUBDIVIDE_MAX_M = 50;
  {
    const src = pts.slice();
    const m = src.length;
    const chords = closed ? m : m - 1;
    const subdivided: Array<[number, number]> = [];
    for (let i = 0; i < chords; i += 1) {
      const a = src[i];
      const b = src[(i + 1) % m];
      subdivided.push(a);
      const len = dist(a, b);
      const k = Math.floor(len / SUBDIVIDE_MAX_M);
      for (let j = 1; j <= k; j += 1) {
        const w = j / (k + 1);
        subdivided.push([a[0] + (b[0] - a[0]) * w, a[1] + (b[1] - a[1]) * w]);
      }
    }
    if (!closed) subdivided.push(src[m - 1]);
    pts.length = 0;
    pts.push(...subdivided);
  }

  const n = pts.length;
  const at = (i: number): [number, number] => {
    if (closed) return pts[((i % n) + n) % n];
    return pts[Math.min(Math.max(i, 0), n - 1)];
  };

  // Widen the step if the requested density would blow the cap. The
  // ring's seam segment counts when closed.
  let totalLen = 0;
  const segCount = closed ? n : n - 1;
  for (let i = 0; i < segCount; i += 1) totalLen += dist(at(i), at(i + 1));
  const effectiveStep = Math.max(stepMeters, totalLen / Math.max(maxPoints - 1, 1));

  const out: Array<[number, number]> = [];
  for (let i = 0; i < segCount; i += 1) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    // Centripetal knots: t increments by sqrt of chord length.
    const t0 = 0;
    const t1 = t0 + Math.sqrt(dist(p0, p1));
    const t2 = t1 + Math.sqrt(dist(p1, p2));
    const t3 = t2 + Math.sqrt(dist(p2, p3));
    const segLen = dist(p1, p2);
    const samples = Math.max(Math.ceil(segLen / effectiveStep), 1);
    // Clamped-end guard: at an open line's ends p0===p1 (or p2===p3),
    // making t1===t0 (or t3===t2). Fall back to the adjacent knot span
    // so the Barry-Goldman weights stay finite.
    const span01 = t1 - t0 || t2 - t1 || 1;
    const span23 = t3 - t2 || t2 - t1 || 1;
    const span12 = t2 - t1 || 1;
    for (let s = 0; s < samples; s += 1) {
      const t = t1 + (span12 * s) / samples;
      // Barry-Goldman pyramidal evaluation of centripetal Catmull-Rom.
      const lerp = (
        a: readonly [number, number],
        b: readonly [number, number],
        ta: number,
        tb: number,
      ): [number, number] => {
        const span = tb - ta || 1;
        const w = (t - ta) / span;
        return [a[0] + (b[0] - a[0]) * w, a[1] + (b[1] - a[1]) * w];
      };
      const a1 = lerp(p0, p1, t0, t0 + span01);
      const a2 = lerp(p1, p2, t1, t1 + span12);
      const a3 = lerp(p2, p3, t2, t2 + span23);
      const b1 = lerp(a1, a2, t0, t2);
      const b2 = lerp(a2, a3, t1, t3);
      out.push(lerp(b1, b2, t1, t2));
    }
  }
  // Close the ring exactly / preserve the open end verbatim.
  out.push(closed ? [...out[0]] : [...pts[n - 1]]);

  return out.map(toLatLon);
}
